import React, { useEffect, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from "immutability-helper";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { WorkflowService } from "../../services/WorkflowService";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Skeleton } from "../ui/skeleton";
import { CheckCircleIcon, XCircleIcon, AlertCircleIcon, GripVerticalIcon } from "lucide-react";
import { cn } from "../../lib/utils";

interface DraggableHeaderCellProps {
  index: number;
  moveColumn: (dragIndex: number, hoverIndex: number) => void;
  children: React.ReactNode;
}

const DraggableHeaderCell: React.FC<DraggableHeaderCellProps> = ({
  index,
  moveColumn,
  children,
}) => {
  const [{ isDragging }, drag] = useDrag({
    type: "DraggableWorkflowColumn",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "DraggableWorkflowColumn",
    hover(item: { index: number }) {
      if (item.index !== index) {
        moveColumn(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => {
        drag(node);
        drop(node);
      }}
      className={cn(
        "flex items-center justify-center cursor-move transition-opacity",
        isDragging && "opacity-50"
      )}
    >
      <GripVerticalIcon className="w-4 h-4 mr-2 text-muted-foreground" />
      {children}
    </div>
  );
};

interface ComparisonRun {
  matched?: boolean;
  actual_output?: Record<string, unknown>;
  duration_ms?: number;
  error_message?: string;
  status?: string;
  workflow_name?: string;
}

interface ComparisonItem {
  test_set_item_id: number;
  input?: Record<string, unknown>;
  expected_output?: Record<string, unknown>;
  runs: Record<string, ComparisonRun>;
}

interface Props {
  testRunIds: number[];
  apiKey: ApiKeyOrGetter;
}

function truncateJson(obj: unknown, maxLen: number = 80): string {
  if (obj === null || obj === undefined) return "—";
  const str = typeof obj === "string" ? obj : JSON.stringify(obj, null, 0);
  return str.length > maxLen ? str.slice(0, maxLen) + "…" : str;
}

const WorkflowTestRunComparison: React.FC<Props> = ({
  testRunIds,
  apiKey,
}) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ComparisonItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [columnOrder, setColumnOrder] = useState<number[]>(() => testRunIds);
  const pageSize = 10;

  useEffect(() => {
    setColumnOrder(testRunIds);
  }, [testRunIds.join(",")]);

  const moveColumn = (dragIndex: number, hoverIndex: number) => {
    const dragColumn = columnOrder[dragIndex];
    setColumnOrder(
      update(columnOrder, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragColumn],
        ],
      })
    );
  };

  useEffect(() => {
    if (!testRunIds.length || testRunIds.length < 2) return;
    setLoading(true);
    const offset = (page - 1) * pageSize;
    WorkflowService.getWorkflowTestRunsComparison(
      testRunIds,
      offset,
      pageSize,
      setLoading,
      apiKey
    ).subscribe(
      (data: { items: ComparisonItem[]; total: number }) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [testRunIds.join(","), page, apiKey]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      {loading ? (
          <div className="space-y-3 py-6">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            No comparison data available. Ensure all selected runs have completed.
          </div>
        ) : (
          <DndProvider backend={HTML5Backend}>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-border hover:bg-transparent">
                    <TableHead className="w-[120px]">Item</TableHead>
                    <TableHead className="w-[200px]">Input</TableHead>
                    {columnOrder.map((runId, index) => (
                      <TableHead key={runId} className="min-w-[180px] text-center">
                        <DraggableHeaderCell index={index} moveColumn={moveColumn}>
                          Run #{runId}
                        </DraggableHeaderCell>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.test_set_item_id} className="border-b border-border/50">
                      <TableCell className="align-top">
                        <span className="text-xs font-mono text-muted-foreground">
                          #{item.test_set_item_id}
                        </span>
                      </TableCell>
                      <TableCell className="align-top">
                        <pre className="text-xs font-mono whitespace-pre-wrap break-all max-w-[200px]">
                          {truncateJson(item.input)}
                        </pre>
                      </TableCell>
                      {columnOrder.map((runId) => {
                        const run = item.runs[String(runId)];
                        if (!run) {
                          return (
                            <TableCell key={runId} className="text-center text-muted-foreground text-xs">
                              —
                            </TableCell>
                          );
                        }
                        const isPass = run.matched === true;
                        const isError = run.status === "ERROR";
                        return (
                          <TableCell key={runId} className="align-top">
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-center gap-1.5">
                                {isError ? (
                                  <AlertCircleIcon className="w-4 h-4 text-orange-500" />
                                ) : isPass ? (
                                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                                ) : (
                                  <XCircleIcon className="w-4 h-4 text-red-500" />
                                )}
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    "text-xs",
                                    isPass && "border-green-300 text-green-700 dark:text-green-400",
                                    isError && "border-orange-300 text-orange-700 dark:text-orange-400",
                                    !isPass && !isError && "border-red-300 text-red-700 dark:text-red-400"
                                  )}
                                >
                                  {isError ? "Error" : isPass ? "Pass" : "Fail"}
                                </Badge>
                              </div>
                              {run.workflow_name && (
                                <div className="text-xs text-muted-foreground truncate" title={run.workflow_name}>
                                  {run.workflow_name}
                                </div>
                              )}
                              {run.duration_ms != null && (
                                <div className="text-xs text-muted-foreground">{run.duration_ms}ms</div>
                              )}
                              {run.error_message && (
                                <div className="text-xs text-red-500 truncate max-w-[160px]" title={run.error_message}>
                                  {run.error_message}
                                </div>
                              )}
                              {run.actual_output && !run.error_message && (
                                <pre className="text-xs font-mono whitespace-pre-wrap break-all max-h-[80px] overflow-y-auto max-w-[160px]">
                                  {truncateJson(run.actual_output, 100)}
                                </pre>
                              )}
                            </div>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <span className="text-sm text-muted-foreground">
                    Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DndProvider>
        )}
    </div>
  );
};

export default WorkflowTestRunComparison;
